import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useBoxTopperSeals, useBoxTopperSettings, useUpdateBoxTopperSettings } from "@/hooks/useBoxTopper";
import { SealTemplateForm } from "@/components/boxTopper/SealTemplateForm";
import { SealPreview } from "@/components/boxTopper/SealPreview";
import { RecentSeals } from "@/components/boxTopper/RecentSeals";
import {
  MAX_SEALS_PER_ORDER,
  OFFER_TERMS_MAX,
  OFFER_TEXT_MAX,
  normalizeItemRule,
  templateOrientation,
  templateProblems,
  templateWithoutBackground,
  verticalLayoutFrom,
  type BoxTopperSettings,
  type ColorMode,
  type SealMode,
  type SealTemplate,
} from "@/utils/boxTopper";

const since = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

interface Draft {
  mode: SealMode;
  color_mode: ColorMode;
  template: SealTemplate;
  offer_text: string;
  offer_terms: string;
  validity_days: number;
  min_size: number;
  skip_item_ids: string;
}

const toDraft = (s: BoxTopperSettings): Draft => {
  const rule = normalizeItemRule(s.item_rule);
  return {
    mode: s.mode,
    color_mode: s.color_mode,
    template: s.template,
    offer_text: s.offer_text,
    offer_terms: s.offer_terms ?? "",
    validity_days: s.validity_days,
    min_size: rule.min_size,
    skip_item_ids: rule.skip_item_ids.join(", "),
  };
};

const parseIds = (text: string) =>
  text
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 0);

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="max-w-prose">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function ChoiceRow({ value, id, title, description }: { value: string; id: string; title: string; description: string }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 has-[[data-state=checked]]:border-action-primary">
      <RadioGroupItem value={value} id={id} className="mt-0.5" aria-describedby={`${id}-desc`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span id={`${id}-desc`} className="block text-xs text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * Box Topper (MMP-316): the printed box seal for every order. The on/off
 * switch saves at once (turning it on is confirmed, and starts from now, never
 * backfilling); everything else is a draft saved with one button, previewed
 * live at real label size.
 */
const BoxTopper = () => {
  const { data: settings, isLoading } = useBoxTopperSettings();
  const update = useUpdateBoxTopperSettings();
  const seals = useBoxTopperSeals();
  const { toast } = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmOn, setConfirmOn] = useState(false);
  const [confirmVertical, setConfirmVertical] = useState(false);

  useEffect(() => {
    if (settings && !draft) setDraft(toDraft(settings));
  }, [settings, draft]);

  const dirty = useMemo(() => {
    if (!settings || !draft) return false;
    const saved = toDraft(settings);
    if (saved.template.background_png_base64 !== draft.template.background_png_base64) return true;
    const key = (d: Draft) => JSON.stringify({ ...d, template: templateWithoutBackground(d.template) });
    return key(saved) !== key(draft);
  }, [settings, draft]);

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading Box Topper
      </p>
    );
  }

  if (!settings || !draft) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Box Topper</h1>
        <p className="text-sm text-muted-foreground">Box Topper is not set up for this store yet.</p>
      </div>
    );
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  const offerTextError =
    draft.offer_text.trim() === ""
      ? "Say what the voucher is for."
      : draft.offer_text.trim().length > OFFER_TEXT_MAX
        ? `Keep it to ${OFFER_TEXT_MAX} characters.`
        : null;
  const termsError = draft.offer_terms.length > OFFER_TERMS_MAX ? `Keep terms to ${OFFER_TERMS_MAX} characters.` : null;
  const validityError =
    !Number.isInteger(draft.validity_days) || draft.validity_days < 1 || draft.validity_days > 365
      ? "Use a whole number of days from 1 to 365."
      : null;
  const problems = [
    ...templateProblems(draft.template),
    ...[offerTextError, termsError, validityError].filter((p): p is string => !!p),
  ];

  const setEnabled = (enabled: boolean) =>
    update.mutate(
      { storeId: settings.store_id, updates: { enabled } },
      {
        onSuccess: () => toast({ title: enabled ? "Box Topper is on" : "Box Topper is off" }),
        onError: (e) => toast({ title: "Could not change Box Topper", description: e.message, variant: "destructive" }),
      },
    );

  const save = () =>
    update.mutate(
      {
        storeId: settings.store_id,
        updates: {
          mode: draft.mode,
          color_mode: draft.color_mode,
          template: draft.template,
          offer_text: draft.offer_text.trim(),
          offer_terms: draft.offer_terms.trim() === "" ? null : draft.offer_terms.trim(),
          validity_days: draft.validity_days,
          item_rule: { min_size: Number.isFinite(draft.min_size) ? draft.min_size : 1, skip_item_ids: parseIds(draft.skip_item_ids) },
        },
      },
      {
        onSuccess: (row) => {
          setDraft(toDraft(row));
          toast({ title: "Box Topper saved", description: "New orders print with these settings." });
        },
        onError: (e) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
      },
    );

  return (
    <div className="space-y-12 pb-24">
      <header>
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Box Topper</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          A printed seal for each pizza box, with a voucher QR your customer scans for their next offer. Prints on the
          kitchen's Brother label printer as orders come in.
        </p>
      </header>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label id="bt-enabled-label" htmlFor="bt-enabled" className="text-sm font-semibold text-foreground">
                Print seals for new orders
              </Label>
              <p id="bt-enabled-desc" className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {settings.enabled
                  ? `On. Every order placed since ${settings.enabled_at ? since.format(new Date(settings.enabled_at)) : "it was turned on"} prints. Orders from before that never print.`
                  : "Off. Nothing prints. Turning it on starts with the next order; older orders are never printed."}
              </p>
            </div>
            <Switch
              id="bt-enabled"
              aria-labelledby="bt-enabled-label"
              aria-describedby="bt-enabled-desc"
              checked={settings.enabled}
              disabled={update.isPending}
              onCheckedChange={(on) => (on ? setConfirmOn(true) : setEnabled(false))}
            />
          </div>
        </CardContent>
      </Card>

      <Section title="Seals" description="How many seals each order gets, the ink on the roll, and the offer every voucher carries.">
        <Card>
          <CardContent className="space-y-6 p-4 sm:p-6">
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-foreground">Seals per order</legend>
              <RadioGroup value={draft.mode} onValueChange={(v) => set("mode", v as SealMode)} className="grid gap-2 sm:grid-cols-2">
                <ChoiceRow value="per_order" id="bt-mode-order" title="One per order" description="A single seal for the whole order." />
                <ChoiceRow
                  value="per_item"
                  id="bt-mode-item"
                  title="One per boxed item"
                  description={`A seal for each sized item, so 2 pizzas get 2 seals. Drinks, sides and dips are skipped. Up to ${MAX_SEALS_PER_ORDER} per order.`}
                />
              </RadioGroup>
            </fieldset>

            {draft.mode === "per_item" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="bt-min-size">Smallest size that gets a seal</Label>
                  <Input
                    id="bt-min-size"
                    type="number"
                    min={1}
                    className="h-11 sm:h-10"
                    value={Number.isFinite(draft.min_size) ? draft.min_size : ""}
                    onChange={(e) => set("min_size", e.target.value === "" ? Number.NaN : Number(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">POS size id. Items without a size (0) never get one.</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bt-skip-ids">Never seal these item ids</Label>
                  <Input
                    id="bt-skip-ids"
                    className="h-11 text-base sm:h-10 sm:text-sm"
                    placeholder="e.g. 412, 413"
                    value={draft.skip_item_ids}
                    onChange={(e) => set("skip_item_ids", e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Sized items that come in their own container, such as a large soda.</p>
                </div>
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-foreground">Label roll</legend>
              <RadioGroup value={draft.color_mode} onValueChange={(v) => set("color_mode", v as ColorMode)} className="grid gap-2 sm:grid-cols-2">
                <ChoiceRow value="black" id="bt-color-black" title="Black only" description="Standard DK-2205 roll. Red parts of the design print black." />
                <ChoiceRow value="black_red" id="bt-color-red" title="Black and red" description="DK-22251 roll loaded. Red parts print red." />
              </RadioGroup>
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
              <div className="space-y-1">
                <Label htmlFor="bt-offer">Offer</Label>
                <Input
                  id="bt-offer"
                  className="h-11 text-base sm:h-10 sm:text-sm"
                  value={draft.offer_text}
                  aria-invalid={!!offerTextError}
                  onChange={(e) => set("offer_text", e.target.value)}
                />
                <p className={`text-xs ${offerTextError ? "text-status-danger" : "text-muted-foreground"}`}>
                  {offerTextError ?? `What the voucher is for. ${draft.offer_text.trim().length}/${OFFER_TEXT_MAX}. Printed as {offer}.`}
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="bt-validity">Valid for (days)</Label>
                <Input
                  id="bt-validity"
                  type="number"
                  min={1}
                  max={365}
                  className="h-11 sm:h-10"
                  value={Number.isFinite(draft.validity_days) ? draft.validity_days : ""}
                  aria-invalid={!!validityError}
                  onChange={(e) => set("validity_days", e.target.value === "" ? Number.NaN : Number(e.target.value))}
                />
                {validityError && <p className="text-xs text-status-danger">{validityError}</p>}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-terms">Terms</Label>
              <Textarea
                id="bt-terms"
                rows={3}
                className="text-base"
                value={draft.offer_terms}
                aria-invalid={!!termsError}
                onChange={(e) => set("offer_terms", e.target.value)}
              />
              <p className={`text-xs ${termsError ? "text-status-danger" : "text-muted-foreground"}`}>
                {termsError ?? `Optional. ${draft.offer_terms.length}/${OFFER_TERMS_MAX}. Printed as {terms} and shown when the voucher is scanned.`}
              </p>
            </div>
          </CardContent>
        </Card>
      </Section>

      <Section
        title="Label design"
        description="Change anything on the seal. The previews use a sample order; real seals fill in each order's details."
      >
        <div className="flex flex-col gap-2 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
            {templateOrientation(draft.template) === "portrait"
              ? "This design is a vertical strip that runs down the lid and folds over the front edge. Start over from the standard vertical layout any time."
              : "This design is the older sideways label. The seal is a vertical strip that runs down the lid and folds over the front edge; switch to the standard vertical layout to match it."}
          </p>
          <Button type="button" variant="outline" className="h-11 shrink-0 sm:h-10" onClick={() => setConfirmVertical(true)}>
            Use the vertical layout
          </Button>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Card className="min-w-0">
            <CardContent className="p-4 sm:p-6">
              <SealTemplateForm template={draft.template} colorMode={draft.color_mode} onChange={(t) => set("template", t)} />
            </CardContent>
          </Card>
          <Card className="order-first min-w-0 self-start lg:order-last lg:sticky lg:top-4">
            <CardContent className="p-4 sm:p-6">
              <SealPreview
                template={draft.template}
                colorMode={draft.color_mode}
                offerText={draft.offer_text}
                offerTerms={draft.offer_terms}
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      <Section title="Recent seals" description="The latest orders' seals. Reprint an order any time; a failed seal can be retried once the printer is fixed.">
        <RecentSeals seals={seals.data ?? []} isLoading={seals.isLoading} />
      </Section>

      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 py-3 pb-safe shadow-sm backdrop-blur-sm">
          <div className="container-app flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            {problems.length > 0 && <p className="text-xs text-status-danger sm:mr-auto">{problems[0]}</p>}
            <Button type="button" variant="ghost" className="h-11" onClick={() => setDraft(toDraft(settings))} disabled={update.isPending}>
              Discard changes
            </Button>
            <Button
              type="button"
              className="h-11 bg-action-primary text-brand-white hover:bg-action-primary/90"
              onClick={save}
              disabled={problems.length > 0 || update.isPending}
            >
              {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Save changes
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOn} onOpenChange={setConfirmOn}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start printing box seals?</AlertDialogTitle>
            <AlertDialogDescription>
              Every order placed from now on prints its seals, each with a real voucher. Orders already placed are not
              printed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEnabled(true)}>Start printing</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmVertical} onOpenChange={setConfirmVertical}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the design with the vertical layout?</AlertDialogTitle>
            <AlertDialogDescription>
              Every text and QR element on the seal is replaced by the standard vertical strip.
              That layout includes the Folsom address, phone number and front-edge copy from the mockup;
              a store at another location must edit those lines in the template editor afterwards.
              {templateOrientation(draft.template) === "landscape" &&
                " The background image is removed, since it was made for the sideways label; upload your logo again."}{" "}
              Seals keep printing the saved design until you save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep this design</AlertDialogCancel>
            <AlertDialogAction onClick={() => set("template", verticalLayoutFrom(draft.template))}>Use the vertical layout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BoxTopper;
